import { useMemo, useState, ReactNode } from "react";
import { Stack } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import type { Components } from 'react-markdown';

import styles from "./Answer.module.css";
import { ChatAppResponse, SpeechConfig } from "../../api";
import userImage from "../../assets/Remote_Experts.png";
import { AnswerFeedback } from "./AnswerFeedback";
import { AnswerFollowupQuestions } from "./AnswerFollowupQuestions";
import { AnswerActions } from "./AnswerActions";
import { CitationPill } from "./CitationPill";
import { findSearchResult } from "./citationHelpers";
import { ShareDocumentDialog } from "../ShareDocument/ShareDocumentDialog";

/**
 * Represents the properties for a chat application's UI component.
 *
 * @interface Props
 *
 * @property {ChatAppResponse} answer - The chat application's response object.
 * @property {number} index - The index position of the current item.
 * @property {SpeechConfig} speechConfig - Configuration settings for speech synthesis or recognition.
 * @property {boolean} [isSelected] - Flag indicating whether the item is selected.
 * @property {boolean} isStreaming - Flag indicating whether the data is currently streaming.
 * @property {function} onCitationClicked - Callback function invoked when a citation link is clicked.
 * @property {function} onThoughtProcessClicked - Callback function invoked when the thought process link is clicked.
 * @property {function} onSupportingContentClicked - Callback function invoked when the supporting content link is clicked.
 * @property {function} [onFollowupQuestionClicked] - Callback function invoked when a follow-up question is clicked.
 * @property {boolean} [showFollowupQuestions] - Flag indicating whether follow-up questions are displayed.
 * @property {boolean} [showSpeechOutputBrowser] - Flag indicating whether the browser-based speech output is displayed.
 * @property {boolean} [showSpeechOutputAzure] - Flag indicating whether the Azure-based speech output is displayed.
 * @property {function} [onFeedbackClicked] - Callback function invoked when feedback is provided.
 */

interface Props {
    answer: ChatAppResponse;
    index: number;
    sessionId?: string;
    speechConfig: SpeechConfig;
    isSelected?: boolean;
    isStreaming: boolean;
    onCitationClicked: (filePath: string) => void;
    onThoughtProcessClicked: () => void;
    onSupportingContentClicked: () => void;
    onFollowupQuestionClicked?: (question: string) => void;
    showFollowupQuestions?: boolean;
    showSpeechOutputBrowser?: boolean;
    showSpeechOutputAzure?: boolean;
    onFeedbackClicked?: (result: any) => void;
}

/**
 * Represents a component that renders an `Answer` with functionalities such as displaying citations,
 * handling feedback clicks, showing follow-up questions, and speech output options.
 *
 * @param {Object} Props - The properties passed into the component.
 * @param {Object} Props.answer - The answer object containing the message content and context with optional follow-up questions and data points.
 * @param {number} Props.index - An index to uniquely identify this instance of the component.
 * @param {Object} Props.speechConfig - Configuration object for handling speech output.
 * @param {boolean} Props.isSelected - Flag indicating if the answer is selected.
 * @param {boolean} Props.isStreaming - Flag indicating if the answer is being streamed.
 * @param {function} Props.onCitationClicked - Callback function invoked when a citation is clicked.
 * @param {function} Props.onThoughtProcessClicked - Callback function invoked when the thought process button is clicked.
 * @param {function} Props.onSupportingContentClicked - Callback function invoked when the supporting content button is clicked.
 * @param {function} Props.onFollowupQuestionClicked - Callback function invoked when a follow-up question is clicked.
 * @param {boolean} Props.showFollowupQuestions - Flag indicating if follow-up questions should be displayed.
 * @param {boolean} Props.showSpeechOutputAzure - Flag indicating if Azure speech output is enabled.
 * @param {boolean} Props.showSpeechOutputBrowser - Flag indicating if browser speech output is enabled.
 * @param {function} Props.onFeedbackClicked - Callback function invoked when feedback (positive/negative) is given.
 *
 * @returns {JSX.Element} - The JSX component representing the structured answer display with interactive elements.
 */
export const Answer = ({
    answer,
    index,
    sessionId,
    speechConfig,
    isSelected,
    isStreaming,
    onCitationClicked,
    onThoughtProcessClicked,
    onSupportingContentClicked,
    onFollowupQuestionClicked,
    showFollowupQuestions,
    showSpeechOutputBrowser,
    showSpeechOutputAzure,
    onFeedbackClicked
}: Props) => {
    const { t } = useTranslation();
    const followupQuestions = answer.context?.followup_questions;
    const messageContent = answer.message.content;
    const missingCitations = answer.context?.missing_citations || [];

    // Single share-dialog state for all CitationPills inside this answer
    // (inline pills + bottom citation list + missing-citations footer).
    const [shareTarget, setShareTarget] = useState<{ url: string; fileName: string } | null>(null);
    const handleShareClicked = (url: string, fileName: string) => setShareTarget({ url, fileName });

    // Sanitize message content for speech output (removes citations, HTML, markdown, and emojis)
    const sanitizedAnswerHtml = useMemo(() => {
        // Remove citation brackets for speech
        let cleanedText = messageContent.replace(/\[([^\]]+)\]/g, '');
        
        // Remove markdown headings (###, ##, #)
        cleanedText = cleanedText.replace(/^#{1,6}\s+/gm, ''); // Removes # at start of lines
        
        // Remove markdown lists (-, *, +, numbered lists)
        cleanedText = cleanedText.replace(/^\s*[-*+]\s+/gm, ''); // Remove bullet points (-, *, +)
        
        // Remove markdown bold/italic formatting (**, __, *, _)
        cleanedText = cleanedText.replace(/\*\*([^*]+)\*\*/g, '$1'); // **bold**
        cleanedText = cleanedText.replace(/__([^_]+)__/g, '$1'); // __bold__
        cleanedText = cleanedText.replace(/\*([^*]+)\*/g, '$1'); // *italic*
        cleanedText = cleanedText.replace(/_([^_]+)_/g, '$1'); // _italic_
        
        // Remove markdown links [text](url) -> keep only text
        cleanedText = cleanedText.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
        
        // Remove markdown code blocks and inline code (`, ```, ~~~)
        cleanedText = cleanedText.replace(/```[\s\S]*?```/g, ''); // Code blocks
        cleanedText = cleanedText.replace(/`([^`]+)`/g, '$1'); // Inline code
        
        // Remove horizontal rules (---, ***, ___)
        cleanedText = cleanedText.replace(/^[-*_]{3,}\s*$/gm, '');
        
        // Remove blockquote markers (>)
        cleanedText = cleanedText.replace(/^\s*>\s+/gm, '');
        
        // Remove emojis (matches most common emoji ranges)
        cleanedText = cleanedText.replace(/[\u{1F600}-\u{1F64F}]/gu, ''); // Emoticons
        cleanedText = cleanedText.replace(/[\u{1F300}-\u{1F5FF}]/gu, ''); // Misc Symbols and Pictographs
        cleanedText = cleanedText.replace(/[\u{1F680}-\u{1F6FF}]/gu, ''); // Transport and Map
        cleanedText = cleanedText.replace(/[\u{1F700}-\u{1F77F}]/gu, ''); // Alchemical Symbols
        cleanedText = cleanedText.replace(/[\u{1F780}-\u{1F7FF}]/gu, ''); // Geometric Shapes Extended
        cleanedText = cleanedText.replace(/[\u{1F800}-\u{1F8FF}]/gu, ''); // Supplemental Arrows-C
        cleanedText = cleanedText.replace(/[\u{1F900}-\u{1F9FF}]/gu, ''); // Supplemental Symbols and Pictographs
        cleanedText = cleanedText.replace(/[\u{1FA00}-\u{1FA6F}]/gu, ''); // Chess Symbols
        cleanedText = cleanedText.replace(/[\u{1FA70}-\u{1FAFF}]/gu, ''); // Symbols and Pictographs Extended-A
        cleanedText = cleanedText.replace(/[\u{2600}-\u{26FF}]/gu, ''); // Misc symbols (⚠️, ☀️, etc.)
        cleanedText = cleanedText.replace(/[\u{2700}-\u{27BF}]/gu, ''); // Dingbats
        
        return DOMPurify.sanitize(cleanedText);
    }, [messageContent]);

    // Helper function to render citation pill inline
    // This function is optimized for streaming - it handles partial/incomplete citations gracefully
    const renderCitationPill = (documentId: string, index: number) => {
        // Early return for empty or invalid citations
        if (!documentId || documentId.trim() === '') {
            return <span key={`citation-${index}`}>[{documentId}]</span>;
        }

        const searchResults = answer?.context?.data_points?.results ?? [];
        const searchResult = findSearchResult(documentId, searchResults);

        return (
            <CitationPill
                key={`citation-${index}`}
                citation={documentId}
                searchResult={searchResult}
                onCitationClicked={onCitationClicked}
                onShareClicked={handleShareClicked}
                inline={true}
            />
        );
    };

    // Helper to process a text fragment and replace citation patterns with pill components
    const processTextForCitations = (text: string): ReactNode[] => {
        const citationRegex = /\[([^\]]+)\]/g;
        const parts: ReactNode[] = [];
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = citationRegex.exec(text)) !== null) {
            // If streaming and the last bracket isn't closed yet, skip (avoid flicker of half citation)
            if (match.index + match[0].length === text.length && isStreaming && !text.trim().endsWith(']')) {
                break;
            }
            if (match.index > lastIndex) {
                parts.push(text.substring(lastIndex, match.index));
            }
            const documentId = match[1];
            parts.push(renderCitationPill(documentId, match.index));
            lastIndex = match.index + match[0].length;
        }
        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }
        return parts;
    };

    // Recursively process children that may contain strings with citation patterns
    const renderChildrenWithCitations = (children: ReactNode | ReactNode[]): ReactNode[] => {
        const flat: ReactNode[] = Array.isArray(children) ? children : [children];
        const transformed: ReactNode[] = [];
        flat.forEach((child, idx) => {
            if (typeof child === 'string') {
                transformed.push(...processTextForCitations(child));
            } else if (Array.isArray(child)) {
                transformed.push(...renderChildrenWithCitations(child));
            } else {
                transformed.push(child);
            }
        });
        return transformed;
    };

    // Override paragraph and list item rendering to inject inline citation pills
    const markdownComponents: Components = useMemo(() => ({
        p: ({ node, children }) => <p>{renderChildrenWithCitations(children)}</p>,
        li: ({ node, children }) => <li>{renderChildrenWithCitations(children)}</li>,
        span: ({ node, children }) => <span>{renderChildrenWithCitations(children)}</span>,
        strong: ({ node, children }) => <strong>{renderChildrenWithCitations(children)}</strong>,
        em: ({ node, children }) => <em>{renderChildrenWithCitations(children)}</em>,
        code: ({ node, children }) => <code>{renderChildrenWithCitations(children)}</code>,
        a: ({ node, children, ...props }) => <a {...props}>{renderChildrenWithCitations(children)}</a>
    }), [messageContent, answer?.context?.data_points?.results, isStreaming]);

    return (
        <div className="flex flex-row items-start gap-2.5 mb-4">
            <img
                className="w-8 h-8 rounded-full bg-white border"
                src={userImage}
                alt="user image"
            />
            <div className="flex flex-col w-full max-w-lg leading-1.5 p-4 shadow-sm rounded-xl rounded-tl-sm bg-white text-sm font-normal text-gray-900">
                <div className="flex">
                    <span className="text-sm font-semibold text-gray-900 grow">
                        WEINIG LifeTime Service Assistent
                    </span>
                    <div className="flex items-center gap-2">
                        <AnswerFeedback onFeedbackClicked={onFeedbackClicked} />
                        <AnswerActions
                            answer={answer}
                            index={index}
                            sessionId={sessionId}
                            speechConfig={speechConfig}
                            isStreaming={isStreaming}
                            onThoughtProcessClicked={onThoughtProcessClicked}
                            onSupportingContentClicked={onSupportingContentClicked}
                            showSpeechOutputAzure={showSpeechOutputAzure}
                            showSpeechOutputBrowser={showSpeechOutputBrowser}
                            sanitizedAnswerHtml={sanitizedAnswerHtml}
                        />
                    </div>
                </div>

                <div className={styles.markdownContent}>
                    <ReactMarkdown
                        children={messageContent}
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[remarkGfm]}
                        components={markdownComponents}
                    />
                </div>

                {/* Display missing citations after streaming completes */}
                {!isStreaming && missingCitations.length > 0 && (() => {
                    // Filter out citations that are already in the response text
                    const filteredMissingCitations = missingCitations.filter(citation => {
                        // Check if this citation appears in the message content as [citation]
                        return !messageContent.includes(`[${citation}]`);
                    });

                    // Only render if there are actually missing citations after filtering
                    if (filteredMissingCitations.length === 0) {
                        return null;
                    }

                    return (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                            <span className="text-xs text-gray-600 font-semibold">
                                {t("answer.additionalSources")}
                            </span>
                            <div className="flex flex-wrap gap-1 mt-2">
                                {filteredMissingCitations.map((citation, idx) => {
                                    const results = answer.context?.data_points?.results || [];
                                    const result = findSearchResult(citation, results);

                                    return (
                                        <CitationPill
                                            key={idx}
                                            citation={citation}
                                            searchResult={result}
                                            onCitationClicked={onCitationClicked}
                                            onShareClicked={handleShareClicked}
                                            inline={false}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                <AnswerFollowupQuestions
                    followupQuestions={followupQuestions || []}
                    onFollowupQuestionClicked={onFollowupQuestionClicked}
                    showFollowupQuestions={showFollowupQuestions}
                />

                {shareTarget && (
                    <ShareDocumentDialog
                        isOpen={true}
                        sharepointUrl={shareTarget.url}
                        fileName={shareTarget.fileName}
                        onDismiss={() => setShareTarget(null)}
                    />
                )}
            </div>
        </div>
    );
};
